/** @graphql cc **/
import { graphql } from 'react-relay';
import { useMemo } from 'react';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	useContentTitlesQuery$data,
	useContentTitlesQuery$variables,
} from './__generated__/useContentTitlesQuery.graphql.ts';

const ContentTitlesQuery = graphql`
	query useContentTitlesQuery($ids: [ID!]!) {
		pageContent: content(ids: $ids, type: "page") {
			nodes {
				id
				title
			}
		}
		folderContent: content(ids: $ids, type: "folder") {
			nodes {
				id
				title
			}
		}
		whiteboardContent: content(ids: $ids, type: "whiteboard") {
			nodes {
				id
				title
			}
		}
		databaseContent: content(ids: $ids, type: "database") {
			nodes {
				id
				title
			}
		}
		embedContent: content(ids: $ids, type: "embed") {
			nodes {
				id
				title
			}
		}
	}
`;

export type UseContentTitlesResult = {
	titlesByContentId: Map<string, string>;
	isLoading: boolean;
};

/**
 * Fetches content titles for a list of content IDs in a single batch query.
 * Queries page, folder, whiteboard, database, and embed content types to support all content type parentIds.
 * Returns a Map of contentId -> title. IDs with no result or missing title map to empty string.
 */
export const useContentTitles = (contentIds: string[]): UseContentTitlesResult => {
	const ids = useMemo(() => {
		const unique = [...new Set(contentIds)].filter(Boolean) as string[];
		return unique;
	}, [contentIds]);

	const { data, loading } = useQueryInterop<
		useContentTitlesQuery$data,
		useContentTitlesQuery$variables
	>(ContentTitlesQuery, {
		variables: { ids },
		skip: ids.length === 0,
	});

	const titlesByContentId = useMemo(() => {
		const map = new Map<string, string>();
		const allNodes = [
			...(data?.pageContent?.nodes ?? []),
			...(data?.folderContent?.nodes ?? []),
			...(data?.whiteboardContent?.nodes ?? []),
			...(data?.databaseContent?.nodes ?? []),
			...(data?.embedContent?.nodes ?? []),
		];
		for (const node of allNodes) {
			if (node?.id != null) {
				map.set(node.id, node.title ?? '');
			}
		}
		return map;
	}, [data]);

	const isLoading = ids.length > 0 && loading;

	return { titlesByContentId, isLoading };
};
